import { ReactNode, useRef, useState, useEffect } from 'react'

import { ChainOfferProduct } from '@common'
import cx from 'classnames'
import { LazyMotion, AnimatePresence, m } from 'framer-motion'
import { useLocale, useTranslations } from 'next-intl'

import { Grid } from '@/features/storefronts/_storeBase/card/v2/OfferCard/shared/ItemsGrid'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { isChainOffer, isStoreSpecial } from '@/features/storefronts/productFilters'

import { BrawlItem, BrawlChainItem } from './BrawlItem'
import { BrawlStarsHero } from './BrawlStarsHero'
import type { OfferCardContentType } from '../../../shared/types'

import styles from './BrawlStarsOfferCardContent.module.scss'

const BrawlStarSingleItemCardContent = ({ product }: OfferCardContentType): ReactNode => {
  return (
    <div className={styles.SingleItemContainer}>
      <div className={styles.Items}>
        <div className={styles.Item} key={`product-${product.id}-content`}>
          <BrawlItem content={product.contents[0]} isSingleItemOffer={true} />
        </div>
      </div>
    </div>
  )
}

const BrawlStarMultipleItemCardContent = ({ product }: OfferCardContentType): ReactNode => {
  const isStoreSpecialOffer = isStoreSpecial(product)
  return (
    <div className={styles.MultiItemContent}>
      {isStoreSpecialOffer && <BrawlStarsHero product={product} />}
      <div className={cx(styles.MultiItemGridContainer, { [styles.StoreSpecialContainer]: isStoreSpecialOffer })}>
        <Grid gameId={'brawlstars'}>
          {product.contents.map((content, index) => (
            <div className={styles.Item} key={`product-${product.id}-content-${index}`}>
              <BrawlItem content={content} isSingleItemOffer={false} isSubItem={true} />
            </div>
          ))}
        </Grid>
      </div>
    </div>
  )
}

type SwipeDirection = 'Next' | 'Previous'

type SwipeButtonProps = {
  direction: SwipeDirection
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  visible: boolean
}

const SwipeButton = ({ direction, onClick, visible }: SwipeButtonProps) => {
  return (
    <button className={cx(styles.SwipeButton, styles[direction], { [styles.Hidden]: !visible })} onClick={onClick}>
      <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M44.5 22.5C44.5 34.6503 34.6503 44.5 22.5 44.5C10.3497 44.5 0.5 34.6503 0.5 22.5C0.5 10.3497 10.3497 0.5 22.5 0.5C34.6503 0.5 44.5 10.3497 44.5 22.5Z"
          fill="black"
        />
        <path
          d="M42 22.5C42 33.2696 33.2696 42 22.5 42C11.7304 42 3 33.2696 3 22.5C3 11.7304 11.7304 3 22.5 3C33.2696 3 42 11.7304 42 22.5Z"
          fill="#E8E8E8"
        />
        <path d="M20.5366 13L30.4361 22.8995L27.6077 25.7279L17.0011 16.5355L20.5366 13Z" fill="black" />
        <path d="M30.435 22.8995L20.5355 32.799L17 29.2635L27.6066 20.0711L30.435 22.8995Z" fill="black" />
        <path
          d="M41.9432 24C41.9808 23.5049 42 23.0047 42 22.5C42 11.7304 33.2696 3 22.5 3C11.7304 3 3 11.7304 3 22.5C3 23.0047 3.01917 23.5049 3.05683 24C3.82277 13.9313 12.2352 6 22.5 6C32.7648 6 41.1772 13.9313 41.9432 24Z"
          fill="white"
        />
      </svg>
    </button>
  )
}

const container = {
  hidden: {
    x: '100%',
  },
  show: {
    x: 0,
    transition: {
      delay: 0.25,
      type: 'spring',
      stiffness: 180,
      damping: 22,
    },
  },
}

type ChainOfferProductProps = {
  product: ChainOfferProduct
}

const BrawlStarChainOfferCardContent = ({ product }: ChainOfferProductProps): ReactNode => {
  const features = () => import('@/animations/features').then((a) => a.default)
  const locale = useLocale()

  const [isFirstItemVisible, setIsFirstItemVisible] = useState(false)
  const [isLastItemVisible, setIsLastItemVisible] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLDivElement>(null)
  const lastItemRef = useRef<HTMLDivElement>(null)

  const t = useTranslations()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === firstItemRef.current) {
            setIsFirstItemVisible(entry.isIntersecting)
          } else if (entry.target === lastItemRef.current) {
            setIsLastItemVisible(entry.isIntersecting)
          }
        })
      },
      { threshold: 0.75 },
    )

    if (firstItemRef.current) {
      observer.observe(firstItemRef.current)
    }
    if (lastItemRef.current) {
      observer.observe(lastItemRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, direction: SwipeDirection) => {
    e.preventDefault()
    e.stopPropagation()
    const scrollAmount = direction === 'Next' ? 154 : -154
    wrapperRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  const useChainHeader = false
  const ChainHeader = (
    <div className={styles.ChainHeader}>
      <div className={styles.GetNowBanner}>
        <StoreTypography tag="p" style="outlined" textSize="sm" className={styles.GetNowBannerTitle}>
          {t('bs_chain_get_now')}
        </StoreTypography>
      </div>
      <div className={styles.UnlockBanner}>
        <StoreTypography
          tag="p"
          style="outlined"
          textSize="sm"
          className={cx(styles.UnlockBannerTitle, styles[locale])}
        >
          {t('bs_chain_unlock_later')}
        </StoreTypography>
      </div>
    </div>
  )

  return (
    <LazyMotion features={features}>
      <div className={styles.MultiItemContent} dir="ltr">
        <BrawlStarsHero product={product} />
        <div className={styles.ChainContainer}>
          {useChainHeader && ChainHeader}
          <div className={styles.ChainCarousel} dir="ltr">
            <SwipeButton
              direction="Previous"
              onClick={(e) => handleClick(e, 'Previous')}
              visible={!isFirstItemVisible}
            />
            <SwipeButton direction="Next" onClick={(e) => handleClick(e, 'Next')} visible={!isLastItemVisible} />
            <AnimatePresence mode="sync">
              <m.div
                className={styles.ChainItemsWrapper}
                ref={wrapperRef}
                variants={container}
                initial="hidden"
                whileInView="show"
                dir="ltr"
              >
                {product.chainOffers.map((offer, index) => (
                  <div
                    className={styles.ChainItemContainer}
                    key={`product-${product.id}-${index}`}
                    ref={index === 0 ? firstItemRef : index === product.chainOffers.length - 1 ? lastItemRef : null}
                  >
                    <svg
                      width="31"
                      height="38"
                      viewBox="0 0 31 38"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={cx(styles.ChainArrow, { [styles.Hidden]: index === product.chainOffers.length - 1 })}
                    >
                      <path
                        d="M15.4581 31.6032L0.5 37L2.52041 7L18.152 14.1016V8L29 23.1556L15.4581 38V31.6032Z"
                        fill="black"
                      />
                      <path
                        d="M17.4462 24.8571L3 29.5L4.5 7L19.4462 11.4286V7L27.5 18L17.4462 29.5V24.8571Z"
                        fill="#F6F6F6"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.808594 32.305L2.67079 4.37199L17.4462 8.74996V0.882812L30.0598 18.1106L15.4462 34.8263V27.6006L0.808594 32.305ZM17.4462 24.8571V29.4999L27.5 17.9999L19.4462 6.99994V11.4285L4.50004 6.99994L3.00004 29.4999L17.4462 24.8571Z"
                        fill="black"
                      />
                    </svg>
                    <BrawlChainItem
                      product={offer as unknown as ChainOfferProduct}
                      assetColorOverride={offer.chainOfferData?.index === 1 ? 'chainOffer' : 'yellow'}
                      showLock={offer.chainOfferData?.index !== 1}
                    />
                  </div>
                ))}
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}

export function BrawlContent({ product, offerCardType }: OfferCardContentType): ReactNode {
  const isMultiItemOffer = product.contents.length > 1
  const isChainOfferCard = isChainOffer(product)

  if (isChainOfferCard) {
    return <BrawlStarChainOfferCardContent product={product as ChainOfferProduct} />
  }
  if (offerCardType === 'special') {
    return <BrawlStarMultipleItemCardContent product={product} />
  }
  if (isMultiItemOffer) {
    return <BrawlStarMultipleItemCardContent product={product} />
  }
  return <BrawlStarSingleItemCardContent product={product} />
}

export function BrawlStarsOfferCardContent({ product, offerCardType }: OfferCardContentType): ReactNode {
  return (
    <div className={styles.Content}>
      <BrawlContent product={product} offerCardType={offerCardType} />
    </div>
  )
}
